/**
 * Check if a route needs re-optimization (stops changed since last optimize).
 */
export const isRouteStale = (route) => {
  if (!route) return false
  if (!route.lastOptimizedAt) return true
  // Allow 5 second tolerance — updatedAt is set by Mongoose on save,
  // which happens slightly after lastOptimizedAt is set client-side
  const updated = new Date(route.updatedAt).getTime()
  const optimized = new Date(route.lastOptimizedAt).getTime()
  return updated - optimized > 5000
}

/**
 * Calculate estimated arrival/departure times from stop drive times.
 * @param {Array} stops - ordered stop array with driveTimeMinutes and serviceMinutes
 * @param {Date|string} leaveAt - departure time
 * @returns {Array} stops with estimatedArrival and estimatedDeparture added
 */
export const calculateEstimatedTimes = (stops, leaveAt) => {
  if (!stops?.length || !leaveAt) return stops || []
  let currentTime = new Date(leaveAt).getTime()

  return stops.map((stop) => {
    // Use whichever drive time field is available (server returns different names
    // depending on whether the route has been optimized or not)
    const driveMinutes = stop.plannedDriveMinutes
      ?? stop.driveTimeMinutes
      ?? stop.estimatedDriveDuration
      ?? stop.trafficDriveMinutes
      ?? 0
    currentTime += driveMinutes * 60000
    const estimatedArrival = new Date(currentTime)

    const serviceMinutes = stop.serviceMinutes || 15
    currentTime += serviceMinutes * 60000
    const estimatedDeparture = new Date(currentTime)

    return { ...stop, estimatedArrival, estimatedDeparture, driveTimeMinutes: driveMinutes }
  })
}

/**
 * Format a time for display (e.g., "7:30 AM").
 */
export const formatTime = (date) => {
  if (!date) return '--'
  const d = new Date(date)
  if (isNaN(d.getTime())) return '--'
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

/**
 * Format stop summary for list item display.
 */
export const formatStopSummary = (stop) => {
  if (!stop) return ''
  const parts = [stop.name || 'Unnamed Stop']
  if (stop.address) parts.push(stop.address)
  return parts.join(' — ')
}

/**
 * Reorder an array by moving item from one index to another.
 * Used by react-beautiful-dnd onDragEnd.
 */
export const reorderStops = (stops, fromIndex, toIndex) => {
  const result = Array.from(stops)
  const [removed] = result.splice(fromIndex, 1)
  result.splice(toIndex, 0, removed)
  return result.map((stop, i) => ({ ...stop, order: i + 1 }))
}
